// Multi-select playlist picker for the media grid's bulk "Add to playlists…"
// action (S4 bulk ops). Pick any number of playlists; confirming appends every
// selected media item to each chosen playlist via POST /v1/playlists/bulk-add.

import { useState } from 'react';
import { Modal } from '../../components/Modal.tsx';
import { Button, Loading, ErrorState, EmptyState } from '../../components/ui.tsx';
import { PlaylistIcon } from '../../onboarding/icons.tsx';
import { usePlaylists } from '../../api/playlists.ts';
import { ApiError } from '../../api/client.ts';
import { plural } from '../../lib/format.ts';

/** Success-toast copy: "Added 3 items to 2 playlists". Pure for unit tests. */
export function addedToPlaylistsMessage(itemCount: number, playlistCount: number): string {
  return `Added ${itemCount} ${plural(itemCount, 'item')} to ${playlistCount} ${plural(playlistCount, 'playlist')}`;
}

/** Error-toast copy. Bulk-add is all-or-nothing: a 422 means a selected item was
 *  deleted out from under us (ids come from our own grid), so ask for a refresh
 *  rather than showing a raw validation string. Pure for unit tests. */
export function bulkAddErrorMessage(e: unknown): string {
  if (e instanceof ApiError && e.status === 422)
    return 'Some items are no longer available — refresh and try again.';
  if (e instanceof ApiError) return e.message;
  return 'Could not add to playlists';
}

export function PlaylistMultiPicker({
  mediaCount,
  busy = false,
  onConfirm,
  onClose,
}: {
  mediaCount: number;
  busy?: boolean;
  onConfirm: (playlistIds: string[]) => void;
  onClose: () => void;
}) {
  const playlists = usePlaylists();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const count = selected.size;

  return (
    <Modal
      title={`Add ${mediaCount} ${plural(mediaCount, 'item')} to playlists`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={count === 0 || busy}
            onClick={() => onConfirm([...selected])}
          >
            {busy ? 'Adding…' : `Add to ${count} ${plural(count, 'playlist')}`}
          </Button>
        </>
      }
    >
      {playlists.isLoading ? (
        <Loading />
      ) : playlists.isError ? (
        <ErrorState message="Could not load playlists." onRetry={() => playlists.refetch()} />
      ) : (playlists.data ?? []).length === 0 ? (
        <EmptyState icon={<PlaylistIcon />} title="No playlists yet">
          <p className="text-dim">Create a playlist first, then add media to it here.</p>
        </EmptyState>
      ) : (
        <div className="pl-multipick">
          {playlists.data!.map((p) => {
            const on = selected.has(p.id);
            return (
              <label key={p.id} className={`pl-multipick-row${on ? ' selected' : ''}`}>
                <input type="checkbox" checked={on} onChange={() => toggle(p.id)} />
                <span className="pl-multipick-name">{p.name}</span>
              </label>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
